// src/ConsumerApp/Screens/Account/views/RfqsView.js

import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { load } from '@cashfreepayments/cashfree-js';
import { theme } from '../../../../assets/theme/theme';
import RfqCard from '../components/RfqCard';

import ClipboardText from '../../../../assets/icons/PClipboardText.svg';
import ShootingStar from '../../../../assets/icons/ShootingStar.svg';
import Phone from '../../../../assets/icons/Phone.svg';
import ChatCircle from '../../../../assets/icons/ChatCircle.svg';
import ToteIcon from '../../../../assets/icons/tote.svg';
import X from '../../../../assets/icons/X.svg';
import ShieldCheck from '../../../../assets/icons/ShieldCheck.svg';
import MagnifyingGlass from '../../../../assets/icons/MagnifyingGlass.svg';
import ArrowLeft from '../../../../assets/icons/ArrowLeft.svg';

import { useGetRfqsByCustomer, useGetRfqById } from '../../../api/rfq/rfq.hooks';
import { useInitiatePayment, useVerifyPaymentStatus, useGetOrderByTransactionId } from '../../../api/payment/payment.hooks';

const IMAGE_BASE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

export default function RfqsView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRfqId = searchParams.get('rfq_id');

  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id || customer?.id;
  const isAuthenticated = Boolean(customerId);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('ALL');
  const [paymentMethod, setPaymentMethod] = useState('ONLINE');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const { data: rawRfqData = [], isLoading: isLoadingList } = useGetRfqsByCustomer({ customerId, rfqs: 'all' });

  const rfqsList = useMemo(() => {
    if (Array.isArray(rawRfqData)) return rawRfqData;
    if (Array.isArray(rawRfqData?.data)) return rawRfqData.data;
    return [];
  }, [rawRfqData]);

  const { data: selectedRfqDetails, isLoading: isLoadingDetails } = useGetRfqById(selectedRfqId);
  const initiatePaymentMutation = useInitiatePayment();
  const verifyPaymentMutation = useVerifyPaymentStatus();
  const getOrderMutation = useGetOrderByTransactionId();

  const activeRfq = selectedRfqDetails || rfqsList.find((r) => (r._id || r.id) === selectedRfqId);
  const items = Array.isArray(activeRfq?.items) ? activeRfq.items : [];
  const totalAmount = Number(activeRfq?.total || activeRfq?.estimated_total || 0);
  const isQuoteReceived = Boolean(activeRfq?.quotation_id || activeRfq?.is_live_rfq);

  const isPaymentCompleted = 
    activeRfq?.status === 'Order Placed Sucessfully' || 
    activeRfq?.status === 'Order Placed Successfully' || 
    activeRfq?.payment_status === 'Paid';

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleSelectRfq = (id) => {
    setSearchParams({ rfq_id: id });
  };

  const handleBackToList = () => {
    setSearchParams({});
  };

  const verifyBackendPaymentStatus = async (orderId) => {
    setIsProcessingPayment(true);
    try {
      const result = await verifyPaymentMutation.mutateAsync({
        order_id: orderId,
        customer_id: customerId,
      });
      
      const status = (result?.status || result?.data?.status || '').toUpperCase();

      if (['SUCCESS', 'COMPLETED', 'PAID'].includes(status)) {
        setPaymentStatus({
          type: 'success',
          navOrderId: orderId,
          message: 'Your payment was successful. Your order is confirmed!',
        });
      } else if (['PENDING', 'PROCESSING', 'IN_PROGRESS'].includes(status)) {
        setPaymentStatus({
          type: 'pending',
          navOrderId: orderId,
          message: 'Your payment is being processed by your bank. We will update you once confirmed.',
        });
      } else if (['CANCELLED', 'USER_DROPPED', 'ABORTED'].includes(status)) {
        setPaymentStatus({ type: 'failed', message: 'Payment was cancelled. No money was deducted.' });
      } else {
        setPaymentStatus({ type: 'failed', message: result?.message || result?.data?.message || 'Payment transaction failed. Please try again.' });
      }
    } catch {
      setPaymentStatus({
        type: 'pending',
        navOrderId: orderId,
        message: 'Unable to verify payment instantly. Please check your order status shortly.',
      });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) return;
    if (totalAmount <= 0) {
      alert('Total amount must be greater than zero to proceed.');
      return;
    }
    
    setIsProcessingPayment(true);

    try {
      const payload = {
        amount: Math.round(totalAmount),
        customerId: customerId,
        hubId: customer?.hubId || 'default',
        projectId: activeRfq?.project_id || null,
        rfq_id: activeRfq?._id,
        purchase_mode: 'RFQ',
        paymentMethod: paymentMethod === 'COD' ? 'COD' : 'ONLINE',
        selected_delivery_type: activeRfq?.delivery_type || 'SCHEDULED',
        delivery_type: activeRfq?.delivery_type || 'SCHEDULED',
      };

      const res = await initiatePaymentMutation.mutateAsync(payload);
      const resData = res?.data?.data || res?.data || res;

      if (paymentMethod === 'COD' || resData?.payment_mode === 'COD') {
        const orderId = resData?.order_id || resData?.orderId;
        setPaymentStatus({
          type: 'cod',
          navOrderId: orderId,
          message: 'Your order is confirmed! Pay in cash upon delivery.',
        });
        setIsProcessingPayment(false);
        return;
      }

      const targetGateway = (resData?.gateway || resData?.provider || 'CASHFREE').toUpperCase();
      const currentOrderId = resData?.order_id || resData?.orderId;

      if (targetGateway === 'CASHFREE') {
        const { payment_session_id } = resData;
        if (!payment_session_id) throw new Error('Invalid Cashfree session received.');

        const cashfree = await load({ mode: 'sandbox' });
        
        cashfree.checkout({
          paymentSessionId: payment_session_id,
          redirectTarget: '_modal'
        }).then((result) => {
          if (result.error) {
            setPaymentStatus({ type: 'failed', message: result.error.message || 'Payment cancelled by user.' });
            setIsProcessingPayment(false);
          }
          if (result.paymentDetails) {
            verifyBackendPaymentStatus(currentOrderId);
          }
        });
      } else {
        throw new Error(`Unsupported payment gateway: ${targetGateway}`);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setPaymentStatus({
        type: 'failed',
        message: err?.response?.data?.message || err?.message || 'Unable to complete checkout.',
      });
      setIsProcessingPayment(false);
    }
  };

  const handleOrderNavigation = async () => {
    if (paymentStatus.type === 'failed' || paymentStatus.type === 'cancelled') {
      setPaymentStatus(null);
      return; 
    }

    const targetTransactionId = paymentStatus.navOrderId || paymentStatus.displayOrderId;
    if (!targetTransactionId) {
      setPaymentStatus(null);
      return;
    }

    setIsProcessingPayment(true);
    try {
      const response = await getOrderMutation.mutateAsync({
        transaction_order_id: targetTransactionId
      });
      const realOrderId = response?.data?.order_id || response?.order_id;
      setPaymentStatus(null);
      if (realOrderId) {
        navigate(`/consumer/account/orders/${realOrderId}`);
      } else {
        navigate(`/consumer/account/orders`);
      }
    } catch {
      setPaymentStatus(null);
      navigate(`/consumer/account/orders`);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const displayedRfqs = useMemo(() => {
    return rfqsList.filter((rfq) => {
      const isOrdered =
        rfq.status === 'Order Placed Sucessfully' ||
        rfq.status === 'Order Placed Successfully';

      if (activeTab === 'ACTIVE' && isOrdered) return false;
      if (activeTab === 'ORDERED' && !isOrdered) return false;

      if (searchQuery.trim() !== '') {
        const q = searchQuery.toLowerCase();
        const matchesNum = (rfq.rfq_number || '').toLowerCase().includes(q);
        const matchesItems = rfq.items?.some((i) =>
          (i.item_name || '').toLowerCase().includes(q)
        );
        return matchesNum || matchesItems;
      }
      return true;
    });
  }, [rfqsList, activeTab, searchQuery]);

  return (
    <>
      <style>{`
        .bb-sub-circle-back-btn {
          width: 40px;
          height: 40px;
          border-radius: 999px;
          border: 1px solid #E8E0D0;
          background-color: #FFFFFF;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          padding: 0;
          margin-right: 12px;
          outline: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
          transition: transform 0.12s ease, background-color 0.12s ease;
          box-sizing: border-box;
        }
        .bb-sub-circle-back-btn:active {
          transform: scale(0.92);
          background-color: #F8FAFC;
        }
        .bb-sub-circle-back-btn img {
          width: 20px;
          height: 20px;
          display: block;
        }
        .bb-sub-mobile-header {
          display: none;
        }
        .bb-sub-desktop-header {
          display: flex;
        }
        @media (max-width: 900px) {
          .bb-sub-desktop-header {
            display: none !important;
          }
          .bb-sub-mobile-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            height: 56px;
            padding: 0 16px;
            background-color: #FFFFFF;
            border-bottom: 1px solid #F1F5F9;
            box-sizing: border-box;
            position: sticky;
            top: 0;
            z-index: 1000;
          }
          .bb-sub-mobile-content {
            padding: 14px 16px 36px 16px !important;
          }
        }
      `}</style>

      {selectedRfqId ? (
        <div>
          {/* Mobile Header: Single RFQ Details */}
          <div className="bb-sub-mobile-header">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button
                type="button"
                className="bb-sub-circle-back-btn"
                onClick={handleBackToList}
                aria-label="Back to RFQ List"
              >
                <img src={ArrowLeft} alt="Back" />
              </button>
              <h2 style={{
                fontFamily: theme.typography.headingLG.fontFamily,
                fontSize: 16,
                fontWeight: '800',
                color: theme.colors.charcoal,
                margin: 0,
              }}>
                {activeRfq?.rfq_number || 'RFQ Details'}
              </h2>
            </div>
          </div>

          <div style={detailsStyles.wrapper} className="bb-sub-mobile-content">
            {isLoadingDetails && !activeRfq ? (
              <div style={detailsStyles.loadingContainer}>Loading request details...</div>
            ) : !activeRfq ? (
              <div style={detailsStyles.emptyBox}>
                <h4 style={{ margin: 0, color: theme.colors.charcoal }}>RFQ Not Found</h4>
                <button
                  type="button"
                  style={detailsStyles.backToListBtn}
                  onClick={handleBackToList}
                >
                  Back to RFQ List
                </button>
              </div>
            ) : (
              <>
                {/* Desktop Detail Header */}
                <div style={detailsStyles.headerRow} className="bb-sub-desktop-header">
                  <button
                    type="button"
                    className="bb-sub-circle-back-btn"
                    onClick={handleBackToList}
                    aria-label="Back to RFQ List"
                  >
                    <img src={ArrowLeft} alt="Back" />
                  </button>
                  <div style={{ flex: 1 }}>
                    <h2 style={detailsStyles.rfqNumberTitle}>
                      {activeRfq.rfq_number || `#${String(selectedRfqId).slice(-6).toUpperCase()}`}
                    </h2>
                    <span style={detailsStyles.rfqDateSubtitle}>
                      {formatDate(activeRfq.createdAt)}
                    </span>
                  </div>

                  <div style={detailsStyles.detailActionGroup}>
                    <a
                      href="https://wa.me/918639065454"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={detailsStyles.detailHelpBtn}
                    >
                      <img src={ChatCircle} alt="chat" style={{ width: 14, height: 14 }} />
                      <span>Chat</span>
                    </a>
                    <a href="tel:+918247895454" style={detailsStyles.detailHelpBtn}>
                      <img src={Phone} alt="call" style={{ width: 14, height: 14 }} />
                      <span>Call</span>
                    </a>
                  </div>
                </div>

                {isQuoteReceived && !isPaymentCompleted && (
                  <div style={detailsStyles.quotePaymentContainer}>
                    <div style={detailsStyles.quoteAlertBanner}>
                      <div style={detailsStyles.quoteAlertLeft}>
                        <div style={detailsStyles.quoteAlertHeader}>
                          <img src={ShootingStar} alt="star" style={detailsStyles.starIcon} />
                          <strong style={detailsStyles.quoteAlertTitle}>Best Quotation Received</strong>
                        </div>
                        <span style={detailsStyles.quoteAlertPrice}>
                          ₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>

                    <div style={detailsStyles.paymentMethodCard}>
                      <h4 style={detailsStyles.paymentSectionTitle}>Select Payment Method</h4>
                      <div style={detailsStyles.paymentOptionsRow}>
                        <div
                          style={{
                            ...detailsStyles.paymentOptionCompact,
                            ...(paymentMethod === 'ONLINE' ? detailsStyles.paymentOptionActive : {}),
                          }}
                          onClick={() => setPaymentMethod('ONLINE')}
                        >
                          <input
                            type="radio"
                            checked={paymentMethod === 'ONLINE'}
                            readOnly
                            style={detailsStyles.radioInput}
                          />
                          <span style={detailsStyles.paymentOptionText}>Pay Online (UPI, Cards, Net Banking)</span>
                        </div>

                        <div
                          style={{
                            ...detailsStyles.paymentOptionCompact,
                            ...(paymentMethod === 'COD' ? detailsStyles.paymentOptionActive : {}),
                            ...(totalAmount > 10000 ? detailsStyles.paymentOptionDisabled : {}),
                          }}
                          onClick={() => { if (totalAmount <= 10000) setPaymentMethod('COD'); }}
                        >
                          <input
                            type="radio"
                            checked={paymentMethod === 'COD'}
                            readOnly
                            disabled={totalAmount > 10000}
                            style={detailsStyles.radioInput}
                          />
                          <div style={detailsStyles.codTextWrapper}>
                            <span style={detailsStyles.paymentOptionText}>Cash on Delivery</span>
                            {totalAmount > 10000 && <span style={detailsStyles.disabledSubText}>Not available &gt; ₹10k</span>}
                          </div>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      style={{ ...detailsStyles.quotePayBtn, opacity: isProcessingPayment ? 0.7 : 1 }}
                      onClick={handleCheckout}
                      disabled={isProcessingPayment}
                    >
                      {isProcessingPayment ? 'Initiating Secure Payment...' : 'Accept Quote & Pay Now'}
                    </button>
                  </div>
                )}

                <div style={detailsStyles.itemsSummaryBox}>
                  <div style={detailsStyles.itemsHeader}>
                    <span>Items Specification ({activeRfq.total_items || items.length})</span>
                  </div>

                  <div style={detailsStyles.itemsList}>
                    {items.map((item, idx) => {
                      const itemName = item.item_name || 'Requested Construction Item';
                      const quantity = item.quantity || 1;
                      const unit = item.UOM || item.uom || 'Units';
                      const unitPrice = Number(item.price_per_unit || item.selling_price || 0);
                      const itemTotal = Number(
                        item.estimated_item_total ||
                          item.total ||
                          item.item_total_after_savings ||
                          unitPrice * quantity
                      );

                      const imageUrl = item.item_image
                        ? `${IMAGE_BASE_URL}${item.item_image}`
                        : null;

                      return (
                        <div key={idx} style={detailsStyles.itemRow}>
                          <div style={detailsStyles.itemThumbnailBox}>
                            {imageUrl ? (
                              <img src={imageUrl} alt={itemName} style={detailsStyles.itemThumbnailImg} />
                            ) : (
                              <img
                                src={ToteIcon}
                                alt="fallback"
                                style={{ width: 22, height: 22, opacity: 0.35 }}
                              />
                            )}
                          </div>

                          <div style={detailsStyles.itemMetaCol}>
                            <span style={detailsStyles.itemNameText}>{itemName}</span>
                            <span style={detailsStyles.itemQuantityText}>
                              Qty: {quantity} {unit}
                              {unitPrice > 0 &&
                                ` · ₹${unitPrice.toLocaleString('en-IN')} / ${unit}`}
                            </span>
                          </div>

                          <div style={detailsStyles.itemPriceCol}>
                            <span style={detailsStyles.itemTotalText}>
                              ₹{itemTotal.toLocaleString('en-IN')}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={detailsStyles.priceBreakdown}>
                    <div style={detailsStyles.breakdownRow}>
                      <span>Subtotal</span>
                      <span>₹{(activeRfq.subtotal || totalAmount).toLocaleString('en-IN')}</span>
                    </div>

                    {Number(activeRfq.tax_amount) > 0 && (
                      <div style={detailsStyles.breakdownRow}>
                        <span>Estimated Tax</span>
                        <span>₹{Number(activeRfq.tax_amount).toLocaleString('en-IN')}</span>
                      </div>
                    )}

                    {Number(activeRfq.delivery_charges) > 0 && (
                      <div style={detailsStyles.breakdownRow}>
                        <span>Freight / Logistics</span>
                        <span>₹{Number(activeRfq.delivery_charges).toLocaleString('en-IN')}</span>
                      </div>
                    )}

                    <div style={detailsStyles.breakdownDivider} />

                    <div style={detailsStyles.breakdownTotalRow}>
                      <strong>Estimated Total</strong>
                      <strong style={{ color: theme.colors.primaryRed || '#C7061F' }}>
                        ₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </strong>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        /* RFQs List View */
        <div>
          {/* Mobile Header: RFQ Hub */}
          <div className="bb-sub-mobile-header">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button
                type="button"
                className="bb-sub-circle-back-btn"
                onClick={() => navigate('/consumer/account', { replace: true })}
                aria-label="Back to Account"
              >
                <img src={ArrowLeft} alt="Back" />
              </button>
              <h2 style={{
                fontFamily: theme.typography.headingLG.fontFamily,
                fontSize: 18,
                fontWeight: '800',
                color: theme.colors.charcoal,
                margin: 0,
              }}>
                RFQs
              </h2>
            </div>
          </div>

          <div style={listStyles.wrapper} className="bb-sub-mobile-content">
            {/* Desktop List Header */}
            <div style={listStyles.headerRow} className="bb-sub-desktop-header">
              <h2 style={listStyles.pageTitle}>My RFQs</h2>
              <span style={listStyles.orderCountText}>{rfqsList.length} total</span>
            </div>

            <div style={listStyles.controls}>
              <div style={listStyles.searchContainer}>
                <img src={MagnifyingGlass} alt="search" style={{ width: 16, height: 16, opacity: 0.6 }} />
                <input
                  type="text"
                  placeholder="Search by RFQ ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={listStyles.searchInput}
                />
                {searchQuery && (
                  <button style={listStyles.clearBtn} onClick={() => setSearchQuery('')}>
                    ✕
                  </button>
                )}
              </div>

              <div style={listStyles.toggleContainer}>
                <button
                  style={{ ...listStyles.toggleTab, ...(activeTab === 'ALL' ? listStyles.activeToggleTab : {}) }}
                  onClick={() => setActiveTab('ALL')}
                >
                  All
                </button>
                <button
                  style={{ ...listStyles.toggleTab, ...(activeTab === 'ACTIVE' ? listStyles.activeToggleTab : {}) }}
                  onClick={() => setActiveTab('ACTIVE')}
                >
                  Active
                </button>
                <button
                  style={{ ...listStyles.toggleTab, ...(activeTab === 'ORDERED' ? listStyles.activeToggleTab : {}) }}
                  onClick={() => setActiveTab('ORDERED')}
                >
                  Ordered
                </button>
              </div>
            </div>

            {isLoadingList ? (
              <div style={listStyles.stateContainer}>Fetching your RFQ records...</div>
            ) : displayedRfqs.length === 0 ? (
              <div style={listStyles.emptyBox}>
                <div style={listStyles.emptyIconCircle}>
                  <img
                    src={ClipboardText}
                    alt="rfq"
                    style={{
                      width: 24,
                      height: 24,
                      filter:
                        'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)',
                    }}
                  />
                </div>
                <h4 style={listStyles.emptyTitle}>No RFQs found</h4>
                <p style={listStyles.emptySubtitle}>
                  {searchQuery
                    ? 'No results matched your search term.'
                    : 'When you submit bulk quote requests, they will appear here.'}
                </p>
              </div>
            ) : (
              <div style={listStyles.cardsList}>
                {displayedRfqs.map((rfq, idx) => {
                  const key = rfq._id || rfq.id || `rfq-${idx}`;
                  return (
                    <RfqCard
                      key={key}
                      rfq={rfq}
                      onPress={() => handleSelectRfq(key)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {paymentStatus && (
        <div style={detailsStyles.statusModalOverlay}>
          <div style={detailsStyles.statusCard}>
            <div style={{ ...detailsStyles.statusIconCircle, backgroundColor: paymentStatus.type === 'failed' ? '#FEE2E2' : '#DCFCE7' }}>
              <img src={paymentStatus.type === 'failed' ? X : ShieldCheck} alt="status" style={{ width: 28, height: 28 }} />
            </div>
            <h3 style={{ margin: '10px 0 6px 0', fontSize: 18, color: theme.colors.charcoal, fontFamily: theme.typography.headingMD.fontFamily }}>
              {paymentStatus.type === 'failed' ? 'Payment Failed' : 'Order Placed!'}
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#636C78', textAlign: 'center', fontFamily: theme.typography.bodySM.fontFamily }}>{paymentStatus.message}</p>
            
            <button
              style={{ ...detailsStyles.statusActionBtn, opacity: isProcessingPayment ? 0.7 : 1 }}
              disabled={isProcessingPayment}
              onClick={handleOrderNavigation}
              type="button"
            >
              {isProcessingPayment ? 'Loading...' : paymentStatus.type === 'failed' ? 'Retry Payment' : 'View Order'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const listStyles = {
  wrapper: { display: 'flex', flexDirection: 'column', gap: 16, width: '100%' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  pageTitle: { ...theme.typography.headingLG, fontSize: 22, color: theme.colors.charcoal, margin: 0, fontWeight: '800' },
  orderCountText: { ...theme.typography.bodySM, fontSize: 13, color: theme.colors.concrete, fontWeight: '600' },
  controls: { display: 'flex', flexDirection: 'column', gap: 12 },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: theme.radius[12],
    padding: '10px 14px',
    gap: 8,
  },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: 14, color: theme.colors.charcoal, background: 'transparent', fontFamily: theme.typography.bodyMD.fontFamily },
  clearBtn: { background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.concrete, fontSize: 12 },
  toggleContainer: {
    display: 'flex',
    backgroundColor: '#F5F3EF',
    borderRadius: 24,
    padding: 3,
    border: '1px solid #FFFFFF',
    width: 'fit-content',
    gap: 4,
  },
  toggleTab: {
    border: 'none',
    background: 'none',
    padding: '6px 16px',
    borderRadius: 20,
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 13,
    color: '#2E4057',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: '700',
  },
  activeToggleTab: { backgroundColor: '#FFFFFF', color: theme.colors.charcoal, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  cardsList: { display: 'flex', flexDirection: 'column', gap: 12, width: '100%' },
  stateContainer: { padding: 40, textAlign: 'center', color: theme.colors.concrete, fontSize: 14, fontFamily: theme.typography.bodyMD.fontFamily },
  emptyBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px dashed #CBD5E1',
    padding: '48px 24px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: '#FFF1F2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  emptyTitle: { ...theme.typography.headingMD, margin: 0, fontSize: 16, fontWeight: '700', color: theme.colors.charcoal },
  emptySubtitle: { ...theme.typography.bodySM, margin: '6px 0 0 0', fontSize: 12.5, color: theme.colors.concrete, maxWidth: 320, lineHeight: '18px' },
};

const detailsStyles = {
  wrapper: { display: 'flex', flexDirection: 'column', gap: 16, width: '100%' },
  loadingContainer: { padding: 48, textAlign: 'center', backgroundColor: '#FFFFFF', borderRadius: 12, border: '1px solid #E2E8F0', fontFamily: theme.typography.bodyMD.fontFamily },
  emptyBox: { padding: 48, textAlign: 'center', backgroundColor: '#FFFFFF', borderRadius: 12, border: '1px solid #E2E8F0' },
  backToListBtn: { marginTop: 12, border: 'none', backgroundColor: '#E9001E', color: '#FFFFFF', padding: '8px 16px', borderRadius: 8, fontWeight: '700', cursor: 'pointer', fontFamily: theme.typography.bodySM.fontFamily },
  headerRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  rfqNumberTitle: { ...theme.typography.headingMD, fontSize: 18, color: theme.colors.charcoal, margin: 0, fontWeight: '800' },
  rfqDateSubtitle: { ...theme.typography.bodySM, fontSize: 12, color: theme.colors.concrete },
  detailActionGroup: { display: 'flex', alignItems: 'center', gap: 8 },
  detailHelpBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.charcoal,
    textDecoration: 'none',
    fontFamily: theme.typography.bodySM.fontFamily,
  },
  quotePaymentContainer: {
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 16,
    border: '1px solid #E2E8F0',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  quoteAlertBanner: {
    backgroundColor: '#FFF1F2',
    border: '1px solid #FECDD3',
    borderRadius: 10,
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
  },
  quoteAlertLeft: { display: 'flex', flexDirection: 'column', gap: 4 },
  quoteAlertHeader: { display: 'flex', alignItems: 'center', gap: 6 },
  starIcon: {
    width: 16,
    height: 16,
    filter: 'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)',
  },
  quoteAlertTitle: { fontSize: 13, color: '#C7061F', fontWeight: '700', fontFamily: theme.typography.bodySM.fontFamily },
  quoteAlertPrice: { fontSize: 20, fontWeight: '800', color: theme.colors.charcoal, fontFamily: theme.typography.headingLG.fontFamily },
  paymentMethodCard: { display: 'flex', flexDirection: 'column', gap: 8 },
  paymentSectionTitle: { margin: 0, fontSize: 11, fontWeight: '700', color: theme.colors.concrete, textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: theme.typography.bodySM.fontFamily },
  paymentOptionsRow: { display: 'flex', gap: 10, flexDirection: 'row', flexWrap: 'wrap' },
  paymentOptionCompact: { 
    flex: 1, 
    minWidth: 140,
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    backgroundColor: '#FFFFFF',
    transition: 'all 0.2s ease',
  },
  paymentOptionActive: { borderColor: '#C7061F', backgroundColor: '#FFF1F2', boxShadow: '0 0 0 1px #C7061F' },
  paymentOptionDisabled: { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#F1F5F9' },
  radioInput: { margin: 0, accentColor: '#C7061F', cursor: 'inherit', width: 16, height: 16 },
  paymentOptionText: { fontSize: 13, fontWeight: '600', color: theme.colors.charcoal, lineHeight: '1.2', fontFamily: theme.typography.bodySM.fontFamily },
  codTextWrapper: { display: 'flex', flexDirection: 'column' },
  disabledSubText: { fontSize: 10, color: '#DC2626', marginTop: 2, fontWeight: '600', fontFamily: theme.typography.bodySM.fontFamily },
  quotePayBtn: { 
    width: '100%', 
    backgroundColor: theme.colors.primaryRed || '#C7061F',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '14px',
    fontSize: 14,
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: theme.typography.bodyMD.fontFamily,
  },
  itemsSummaryBox: { backgroundColor: '#FFFFFF', borderRadius: 12, border: '1px solid #CBD5E1', overflow: 'hidden' },
  itemsHeader: { backgroundColor: '#F8F6F2', padding: '12px 18px', borderBottom: '1px solid #CBD5E1', fontSize: 13, fontWeight: '700', color: theme.colors.charcoal, fontFamily: theme.typography.bodySM.fontFamily },
  itemsList: { display: 'flex', flexDirection: 'column' },
  itemRow: { display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #F1F5F9', gap: 12 },
  itemThumbnailBox: { width: 44, height: 44, borderRadius: 8, backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' },
  itemThumbnailImg: { width: '90%', height: '90%', objectFit: 'contain' },
  itemMetaCol: { display: 'flex', flexDirection: 'column', flex: 1, gap: 3, minWidth: 0 },
  itemNameText: { fontSize: 13.5, fontWeight: '600', color: theme.colors.charcoal, fontFamily: theme.typography.bodyMD.fontFamily },
  itemQuantityText: { fontSize: 12, color: theme.colors.concrete, fontFamily: theme.typography.bodySM.fontFamily },
  itemPriceCol: { flexShrink: 0 },
  itemTotalText: { fontSize: 14, fontWeight: '700', color: theme.colors.charcoal, fontFamily: theme.typography.bodyMD.fontFamily },
  priceBreakdown: { padding: '16px 18px', backgroundColor: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: 8 },
  breakdownRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: theme.colors.concrete, fontFamily: theme.typography.bodySM.fontFamily },
  breakdownDivider: { height: 1, backgroundColor: '#CBD5E1', margin: '4px 0' },
  breakdownTotalRow: { display: 'flex', justifyContent: 'space-between', fontSize: 15, color: theme.colors.charcoal, fontFamily: theme.typography.headingMD.fontFamily },
  statusModalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  statusCard: { backgroundColor: '#FFFFFF', borderRadius: 12, padding: 24, width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', alignItems: 'center' },
  statusIconCircle: { width: 56, height: 56, borderRadius: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6 },
  statusActionBtn: { backgroundColor: '#E9001E', color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '10px 20px', width: '100%', fontWeight: '700', cursor: 'pointer', fontFamily: theme.typography.bodyMD.fontFamily },
};